/** @jsx jsx */
import React, { useMemo, useState } from "react";
import { Row, Col, Card, Typography, Skeleton } from "antd";
import {
  jsx,
  Box,
  Flex,
  Avatar,
  Link,
  Image,
} from "theme-ui";
import { StaticImage } from "gatsby-plugin-image";
import { Highlight } from "react-instantsearch-dom";
import ContribStatus from "./contribStatus";
import { DATASOURCE_DETAILS, DATASOURCE_ICON_LIMIT } from '../DatasourceDetails';
import GithubIcon from "../../../img/avatars/github.png";
import Action from "../Action";

const { Paragraph } = Typography;

const maturityBox = (maturity) => {
  let boxClass = "none";
  switch (maturity) {
    case "EXPERIMENTAL":
      boxClass = "red";
      break;
    case "BETA":
      boxClass = "orange";
      break;
    case "PRODUCTION":
      boxClass = "green";
      break;
    default:
      break;
  }
  return <Box className={`contrib-stat ${boxClass}`} />;
};

const isBrowser = () => typeof window !== "undefined";

const View = ({ dataset, packageName, filters, version }) => {
  const [filterUrl, setFilterUrl] = useState('');

  useMemo(() => {
    if (filters && filters.subFilterValues && filters.subFilterValues.length > 0) {
      setFilterUrl(`?filterType=${filters.filterType}&gotoPage=${isBrowser() && window.isSubFilterEquals ? filters.gotoPage : 1}&showFilters=${filters.showFilters}&viewType=${filters.viewType}&subFilterValues=${filters.subFilterValues.join(',')}`);
    } else if (filters) {
      setFilterUrl(`?filterType=${filters.filterType}&gotoPage=${isBrowser() && window.isSubFilterEquals ? filters.gotoPage : 1}&showFilters=${filters.showFilters}&viewType=${filters.viewType}`);
    } else {
      setFilterUrl('');
    }
  }, [filters, filters.subFilterValues, isBrowser()]);
  const path = version?`/legacy/${version}/expectations`:'/expectations'
  const res = (
    <Flex
      sx={{
        flexDirection: "column",
        padding: ["8px", "16px", "16px"],
        paddingBottom: "0px",
        backgroundColor: "#FAFAFA",
      }}
    >
      {dataset.map((hit) => (
        <Col key={hit.objectID} md={24} xs={24} sm={24}>
          <Card
            sx={{
              backgroundColor: "#FFFFFF",
              marginBottom: "16px",
              border: "1px solid #D9D9D9",
              borderRadius: "8px",
            }}
            bordered={false}
          >
            <Row sx={{ marginBottom: "8px" }}>
              <Flex
                sx={{
                  alignItems: "baseline",
                  gap: ["8px", "16px", "16px"],
                  flexDirection: ["column", "row"],
                  width: "100%"
                }}
              >
                <Flex sx={{ alignItems:['flex-start'], gap: "8px", width:['100%', null, '50%', 'max-content']}}>
                  <Box pt={2}>
                  {maturityBox(hit.library_metadata.maturity)}
                  </Box>
                  <Box pl={2}>
                  {packageName ?
                    <strong className="expec-labels" sx={{ overflowWrap: "anywhere" }}>
                      <Link sx={{ color: "#262626", fontSize: ["12px", "14px"] }}
                        href={`${packageName.toLowerCase().split(' ').join('_')}/expectations/${hit.description.snake_name}${filterUrl}`}>
                        {hit.description.snake_name}
                        <span sx={{ fontWeight: 400 }}> {hit.exp_type ? ` (${hit.exp_type})` : ''} </span>
                      </Link>
                    </strong> :
                    <strong className="expec-labels" sx={{ overflowWrap: "anywhere" }}>
                      <Link sx={{ color: "#262626", fontSize: ["12px", "14px"] }}
                        href={`${path}/${hit.description.snake_name}${filterUrl}`}>
                        <Highlight attribute="description.snake_name" hit={hit} tagName="strong" />
                        <span sx={{ fontWeight: 400 }}> {hit.exp_type ? ` (${hit.exp_type})` : ''} </span>
                      </Link>
                    </strong>}
                    </Box>
                </Flex>
                <Box>
                  {hit.library_metadata.contributors && hit.library_metadata.contributors.length > 0 && hit.library_metadata.contributors.map((creator) => (
                    <a key={creator}
                      target="_blank"
                      href={`https://github.com/${creator === "@great_expectations" ? "great-expectations" : creator.substring(1)}`}
                      rel="noreferrer"
                    >
                      <Avatar
                        src={
                          `https://github.com/${creator === "@great_expectations" ? "great-expectations" : creator.substring(1)}.png`
                        }
                        onError={({ currentTarget }) => {
                          const currentElement = currentTarget;
                          currentElement.onerror = null;
                          currentElement.src = GithubIcon;
                        }}
                        size="24"
                        shape="circle"
                        backgroundColor="white"
                      />
                    </a>
                  ))}
                </Box>
                <Box ml="auto" sx={{display:['none', 'inherit']}}>
                  <Link
                    href={`${path}/${hit.description.snake_name}${filterUrl}`}
                    sx={{
                      borderRadius: "4px",
                      border: "1px solid #D9D9D9",
                      backgroundColor: "orangeV2",
                      color: "#FFFFFF",
                      fontSize: "14px",
                      fontWeight: "normal",
                      padding: "4px 16px",
                      '&:hover':{
                        color:'#FFFFFF'
                      }
                    }}>
                    See More
                  </Link>
                  </Box>
                
              </Flex>
            </Row>
            {hit.description.short_description && <Row
              sx={{
                fontSize: "12px",
                marginBottom: "8px",
              }}
            >
              <Paragraph ellipsis={{ rows: 3 }}>
                {" "}
                <Highlight attribute="description.short_description" hit={hit} tagName="strong" />
              </Paragraph>
            </Row>
            }

            <Row sx={{ marginBottom: "16px", fontSize: "12px" }}>
               <Col lg={{ span: 12 }} xs={{ span: 0 }} sm={{ span: 11 }}>
                <Flex sx={{ alignItems: "center"}}>
                  <strong className="expec-labels">Tags: </strong>
                  <Flex
                    sx={{
                      marginLeft: "16px",
                      gap: "8px",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    {hit.library_metadata.tags.map((tag) => (
                      <span key={tag} className="tags-pill">{tag}</span>
                    ))}
                    <span sx={{ color: "#595959", fontSize: "14px" }}>
                      {hit.library_metadata.tags.length === 0 ? "None" : null}
                    </span>
                  </Flex>
                </Flex>
              </Col>
              <Col lg={{ span: 12 }} xs={{ span: 22 }} sm={{ span: 11 }}>
                <Flex sx={{ alignItems: "center"}}>
                  <strong className="expec-labels-support" >Support: </strong>
                  <Flex
                    sx={{
                      marginLeft: "16px",
                    }}
                  >
                  {hit.engineSupported.length === 0 ? (
                    <Box>
                      <span className="support-pill">
                        <StaticImage src="../../../images/exclamation.svg" alt="absent" />Support Needed
                      </span>
                    </Box>
                  ) : (
                    <Flex  sx={{
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "2px"
                    }}>
                      {DATASOURCE_DETAILS.slice(0, DATASOURCE_ICON_LIMIT)?.map((img) => (
                        <div key={img.image} className="datasource-name">
                          {hit.engineSupported.findIndex(backend => backend === img.name) !== -1 ?
                            <Image src={img.image} variant="avatar" sx={{ width: "20px", margin: "0" }} alt={img.name} />
                            : null}
                          <span className="datasource-tooltip">{img.displayName}</span>
                        </div>
                      )
                      )}
                      {hit.engineSupported.length > DATASOURCE_ICON_LIMIT ? (<div className="image-hover-div">
                        <div className="hover-image" >
                          {DATASOURCE_DETAILS.slice(DATASOURCE_ICON_LIMIT).map((img) => (
                            <div key={img.image} className="">
                              {hit.engineSupported.findIndex(backend => backend === img.name) !== -1 ?
                                <Image src={img.image} variant="avatar" sx={{ width: "20px", margin: "0 8px" }} alt={img.name} />
                                : null}
                            </div>
                          )
                          )}
                        </div>
                        <div className="btn-show">+{Math.abs(hit.engineSupported.length - DATASOURCE_ICON_LIMIT)}</div>
                      </div>) : null
                      }

                    </Flex>
                  )}
                </Flex>
              </Flex>
            </Col>
           </Row>
           <Box sx={{display:['none', 'inherit']}}>

              <Row gutter={16} sx={{ fontSize: "12px" }}>
                <Col span={24} sx={{ marginBottom: "8px" }}>
                  <strong
                    className="expec-labels"
                    sx={{ "marginBottom": "8px" }}
                    >
                    Contribution status:{" "}
                  </strong>
                </Col>
                <Col span={8}>
                  <ContribStatus
                    data={hit}
                    contribType="Experimental"
                    />
                </Col>
                <Col span={8}>
                <ContribStatus
                  data={hit}
                  contribType="Beta"
                  />
                </Col>
                <Col  span={8}>
                <ContribStatus
                  data={hit}
                  contribType="Production"
                />
                </Col>
              </Row>
            </Box>
            <Box sx={{display:['inherit', 'none']}}>
              <Row >
                <Col span={24}>
                  <Link
                    ml="auto"
                    href={`/expectations/${hit.description.snake_name}${filterUrl}`}
                    sx={{
                      width: "100%",
                      borderRadius: "4px",
                      border: "1px solid #D9D9D9",
                      backgroundColor: "orangeV2",
                      color: "#FFFFFF",
                      fontSize: "14px",
                      fontWeight: "normal",
                      padding: "4px 16px",
                      textAlign: "center",
                      display: "inline-block",
                    }}>
                    See More
                  </Link>
                </Col>
              </Row> 
              </Box>
          
          </Card>
        </Col>
      ))}
      {dataset.length === 0 && <Flex sx={{ alignItems:'center', justifyContent:'center',}}>
        <Box sx={{minWidth:['270px', 'max-content']}}>
        <Action
        label="Can't find the Expectation you need? Create an Expectation."
        url="https://docs.greatexpectations.io/docs/guides/expectations/creating_custom_expectations/overview/"
        style="secondaryV2"
        fontsize="14px"
        sx={{
          "&:hover": {
            cursor: "pointer",
          },
        }} />
        </Box>
        </Flex>}
    </Flex>
  );

  return res;

};


class CardView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoaded: true,
    };
  }

  componentDidMount() {
    this.setState({
      isLoaded: true,
    });
  }

  shouldComponentUpdate(nextProps) {
    const { screenSize, dataset } = this.props;
    if (nextProps.screenSize !== screenSize) {
      return true;
    }
    if (nextProps.dataset.length !== dataset.length) {
      return true;
    }
    // check for same length but different data
    const nextPropExpArr = nextProps.dataset.length === 0 ? [] : nextProps.dataset.map(obj => obj._highlightResult.description.snake_name);
    const newPropExpArr = dataset.length === 0 ? [] : dataset.map(obj => obj._highlightResult.description.snake_name);
    if (nextPropExpArr !== newPropExpArr) {
      return true;
    }
    return false;
  }

  render() {
    const { dataset, screenSize, packageName, filters, version } = this.props;
    if (typeof window !== "undefined") {
      if (filters.subFilterValues && window.expectationsFilter && (filters.subFilterValues.length || window.expectationsFilter.length)) {
        if (filters.subFilterValues.length === 0 && window.expectationsFilter.length === 0) {
          window.isSubFilterEquals = true;
        } else if (filters.subFilterValues.length === 0) {
          window.isSubFilterEquals = false;
        } else if (filters.subFilterValues.length === window.expectationsFilter.length) {
          if (window.expectationsFilter.map(x => filters.subFilterValues.includes(x)).includes(false)) {
            window.isSubFilterEquals = false;
          } else {
            window.isSubFilterEquals = true;
          }
        } else {
          window.isSubFilterEquals = false;
        }
      }
      filters.subFilterValues = window.expectationsFilter;
    }
    const {
      isLoaded
    } = this.state;
    if (!isLoaded) {
      return (
        <Row sx={{ marginTop: "32px" }}>
          <Skeleton paragraph={{ rows: 4 }} />
        </Row>
      );
    }
    return (
      <View dataset={dataset} mobileView={screenSize} packageName={packageName} filters={filters} version={version}/>
    );
  }
}

export default CardView;
